import React, { useState, useMemo, useEffect } from 'react'

import { getErrorMessage } from 'utils'

import { useSnackbar } from 'contexts'
import { useProductDefaultTagMutations, useTagProducts } from 'hooks/api'
import type { TagShopProduct } from 'hooks/api/useTagProducts'
import { isTagProduct } from 'constants/tagShopProductTypes'

import { Stack, Typography } from '@mui/material'

import { Modal, Button } from '@repo/ui'
import { ProductPreview, SearchFilter } from 'components'


interface SetDefaultTagModalProps {
  product: any,
  open: boolean
  onClose?: () => void
  refetch?: () => void
  /**
   * VRG-282 PROMPT MODE — pre-selects the tag and skips the picker entirely, turning this from
   * "choose a default tag" into "make THIS tag the default?".
   *
   * Extended rather than forked into a second modal: both flows write the same
   * `PUT /products/:id/default-tag`, and two modals over one endpoint is exactly how the two
   * directions of the product↔tag relationship drift apart.
   */
  tagProduct?: TagShopProduct | null
  /**
   * Explanatory line above the tag. Prompt mode needs to say WHY it is asking.
   *
   * A node, not a string, so a caller can put emphasis or a "Learn more" link inside the
   * sentence rather than bolting one on after it. No caller does yet — there is no published
   * docs page about default tags to point at.
   */
  description?: React.ReactNode
  title?: string
  /** Prompt mode declines with "Not now" — "Cancel" would imply it undoes the binding. */
  cancelLabel?: string
  confirmLabel?: string
}

export const SetDefaultTagModal = (props: SetDefaultTagModalProps) => {
  const {
    product,
    open,
    onClose = () => { /* default empty handler */ },
    refetch,
    tagProduct = null,
    description,
    title = 'Set default tag',
    cancelLabel = 'Cancel',
    confirmLabel = 'Set',
  } = props
  const [selectedTag, setSelectedTag] = useState<TagShopProduct | null>(tagProduct)

  // Re-seed on open. The modal stays MOUNTED between openings at every call site (it is rendered
  // unconditionally with an `open` prop), so without this a prompt-mode instance would keep the
  // tag from the previous package rather than the one just bound.
  useEffect(() => {
    if (open) setSelectedTag(tagProduct)
  }, [open, tagProduct?.id])

  // Real VERA tag catalog from the shop endpoint. There is always at least one VERA
  // tag available, so there is no "empty" state — the picker always renders.
  // NOTE: in local dev GET /shop/products may return [] until the tag catalog is
  // seeded; that's a dev-data gap, not a real empty state. TODO(VERA-455): ensure the
  // VERA tag products are seeded/served in dev so this picker populates.
  const tagProductsParams = useMemo(() => ({ limit: 100 }), [])
  const { data } = useTagProducts(tagProductsParams)
  // The Shopify tag-shop catalog carries non-tag hardware (the Vera Tag Desk Reader)
  // alongside actual tags — this picker is for choosing a TAG, so hardware is excluded.
  // Was a strict `category_type === 'Tag'`; the shared predicate excludes hardware instead,
  // which also keeps the picker populated against the API's DEV fallback catalog (it
  // returns `category_type: null`, so the strict test emptied this list entirely).
  const tagShopProducts = (data?.products ?? []).filter(isTagProduct)

  const { open: notification } = useSnackbar()

  const { setDefaultTag, isLoading } = useProductDefaultTagMutations({
    onSuccess: () => {
      handleClose()
      refetch?.()
    },
    onError: error => {
      const message = getErrorMessage(error)
      notification({
        type: 'error',
        title: message,
      })
    },
  })

  const handleSetDefaultTag = () => {
    if (!product || !selectedTag) return

    // Guard the IDS, not just the objects. Prompt mode is handed whatever the caller had
    // selected, and an object without an `id` used to sail past the truthiness check above and
    // send `/products/admin/undefined/default-tag` — a request that can only fail, and whose
    // failure surfaced as a raw TypeError. Say what is wrong instead of asking the server.
    if (!product.id || !selectedTag.id) {
      notification({
        type: 'error',
        title: 'This product could not be identified. Close this and try again.',
      })
      return
    }

    setDefaultTag({
      product_id: product.id,
      tag_shop_product_id: selectedTag.id,
    })
  }

  const handleClose = () => {
    onClose()
  }

  return (
    <Modal
      title={title}
      open={open}
      onClose={handleClose}
      actions={
        <>
          <Button
            variant="secondary"
            onClick={handleClose}
          >
            {cancelLabel}
          </Button>

          <Button
            variant="primary"
            disabled={selectedTag == null || isLoading}
            onClick={handleSetDefaultTag}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <Stack spacing={6}>
        {description && (
          <Typography variant='body3' color='text.secondary' sx={{ lineHeight: 1.6 }}>
            {description}
          </Typography>
        )}
        {/* No product preview: the modal is always opened for a specific product, so
            which product is being tagged is implied by context (the product page /
            the clicked row). Showing a product card here is redundant. */}
        {/* Pick a tag: just the "Select tag" label + search initially; typing/focusing
            opens a popover BELOW with the filtered ProductPreview rows (the shared
            SearchFilter notFilter+singleSelect pattern used elsewhere). Hidden once a
            tag is chosen — the Selected-tag card's "Change" button brings it back. */}
        {!selectedTag && (
          <SearchFilter
            notFilter
            singleSelect
            column={{ headerName: 'Select tag', field: 'tag' } as any}
            rows={tagShopProducts as any}
            filter={[] as any}
            searchParams={{ fields: ['title', 'sku'], preview: 'products' }}
            onChange={((item: any) => setSelectedTag(item)) as any}
          />
        )}
        {selectedTag && (
          <Stack spacing={4}>
            <Typography variant='body2'>Selected tag</Typography>
            {/* The "Change" affordance now lives in the shared Preview (onChange) —
                clearing the pick re-opens the search above. Withheld in prompt mode: the
                question there is about ONE specific tag, so swapping it would answer a
                different question than the one asked. */}
            <ProductPreview
              product={selectedTag as any}
              onChange={tagProduct ? undefined : () => setSelectedTag(null)}
            />
          </Stack>
        )}
      </Stack>
    </Modal>
  )
}
